import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, CheckCircle, AlertCircle, Play } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const AdminModuleDurationFix: React.FC = () => {
  const navigate = useNavigate();
  const [isExecuting, setIsExecuting] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const executeRecalculation = async () => {
    setIsExecuting(true);
    setError(null);
    setResult(null);

    try {
      const { data, error: funcError } = await supabase.functions.invoke(
        'recalculate-module-durations',
        { body: { courseId: null } } // null = all courses
      );

      if (funcError) throw funcError;
      
      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Recalculation failed');
    } finally {
      setIsExecuting(false);
    }
  };

  return (
    <div className="container mx-auto py-8 max-w-4xl">
      <Button 
        variant="outline" 
        onClick={() => navigate('/admin')}
        className="mb-6"
      >
        ← Back to Admin
      </Button>

      <Card>
        <CardHeader>
          <CardTitle>Module Duration Recalculation</CardTitle>
          <CardDescription>
            Recalculate estimated duration for all course modules based on content, quizzes, and practice exercises
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Info Section */}
          <Alert>
            <AlertDescription>
              This will calculate duration for all modules using:
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Reading time: 200 words per minute</li>
                <li>Quiz time: 2 minutes per question</li>
                <li>Practice time: 10 minutes per exercise</li>
              </ul>
            </AlertDescription>
          </Alert>

          {/* Execute Button */}
          <Button
            onClick={executeRecalculation}
            disabled={isExecuting}
            size="lg"
            className="w-full"
          >
            {isExecuting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Recalculating All Modules...
              </>
            ) : (
              <>
                <Play className="mr-2 h-4 w-4" />
                Execute Recalculation
              </>
            )}
          </Button>

          {/* Results */}
          {result && (
            <Alert className="border-green-500 bg-green-50 dark:bg-green-950">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription>
                <div className="space-y-2">
                  <p className="font-semibold">Recalculation Complete!</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>Total Processed: {result.processedModules || 0}</div>
                    <div>Successfully Updated: {result.updatedModules || 0}</div>
                    <div>Errors: {result.errorCount || 0}</div>
                  </div>
                  {result.errors && result.errors.length > 0 && (
                    <details className="mt-2">
                      <summary className="cursor-pointer text-sm font-medium">
                        View Errors ({result.errors.length})
                      </summary>
                      <div className="mt-2 space-y-1 text-xs">
                        {result.errors.map((err: any, idx: number) => (
                          <div key={idx} className="text-red-600">
                            {err.moduleId}: {err.error}
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Error */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Info */}
          <div className="text-sm text-muted-foreground space-y-2">
            <p><strong>Note:</strong> This operation may take several minutes for large databases.</p>
            <p>The auto-calculation trigger is now active and will automatically calculate duration for new modules.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminModuleDurationFix;
